/** The AI assistant's settings - ADR-0192, a tab of Settings that only the company Admin has.
 *
 * **Everything is off, and the screen says so before anything else.** The assistant is on for a
 * person only when three things hold (the add-on is in the agreement, a provider is connected, and
 * an Admin has switched it on), and the server names the first one missing; this screen prints that
 * sentence and never one of its own. No provider is called from anywhere in this product today:
 * the connection test ends `assistant_egress_unavailable`, which is the truth, and it is shown as
 * the answer rather than hidden behind a cheerful "connected".
 *
 * **Bring your own model.** EmitIQ names no provider and bundles none. The provider is two API
 * families (a request shape), an endpoint, a model name and rates the Admin enters; the spend shown
 * is *estimated from those rates*, and says so beside every amount (the server's `basis`).
 *
 * **The key is write-only.** The input is a password field, is cleared on success, and nothing on
 * this screen can read it back; the server answers *set* or *not set* and the date.
 *
 * **Honest controls.** Without the add-on the form is disabled and the reason is beside it (design
 * rule 6: a control a role cannot use is shown with its reason, never active). Spend analysis is
 * open in every state, because an Admin must always be able to see what was spent.
 *
 * A non-Admin never reaches the requests: the screen is told whose decision this is and asks for
 * nothing (the server would refuse every read).
 */

import { useState } from "react";
import type { FormEvent } from "react";

import { useMe } from "../api/auth";
import {
  ASSISTANT_SPEND_EXPORT,
  useAssistantConfig,
  useAssistantConnectionTest,
  useAssistantSpend,
  useRemoveAssistantCredential,
  useSaveAssistantConfig,
  useSetAssistantCredential,
} from "../api/assistant";
import type { AssistantConfig, AssistantConfigInput, AssistantSpend } from "../api/assistant";
import { apiUrl } from "../api/client";
import { useReader } from "../api/profile";
import { useOrganization } from "../api/queries";
import { FieldError } from "../components/FieldError";
import { Loaded, WriteFailed } from "../components/Loaded";
import { useFocusRefusal } from "../components/useFocusRefusal";
import { useFormChecks } from "../components/useFormChecks";
import { Panel } from "../components/Panel";
import { InfoPopover } from "../components/ui";
import { StatusPill } from "../components/StatusPill";
import { instantDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import own from "./AssistantSettings.module.css";
import form from "./Profile.module.css";
import styles from "./Screen.module.css";
import { ScreenFrame } from "./ScreenFrame";
import { PersonRef } from "./PersonRef";
import { useScreen } from "./useScreen";

type Family = AssistantConfigInput["adapter_family"];
type Auth = AssistantConfigInput["auth_style"];
type CapChoice = AssistantConfigInput["cap_choice"];
type PermittedRole = NonNullable<AssistantConfigInput["allowed_roles"]>[number];

const FAMILIES: ReadonlyArray<{ value: Family; label: string }> = [
  { value: "openai_compatible", label: "OpenAI-compatible API" },
  { value: "anthropic_messages", label: "Anthropic Messages API" },
];
const AUTH_STYLES: ReadonlyArray<{ value: Auth; label: string }> = [
  { value: "bearer", label: "Bearer token" },
  { value: "api_key_header", label: "API key header" },
  { value: "anthropic_key_header", label: "Anthropic key header" },
];
const CAPS: ReadonlyArray<{ value: CapChoice; label: string }> = [
  { value: "unset", label: "Not decided yet" },
  { value: "capped", label: "Monthly cap" },
  { value: "no_cap", label: "No cap" },
];
/** The roles an Admin may let use the assistant. An Auditor never is; the Admin always may. */
const ROLES: ReadonlyArray<{ value: PermittedRole; label: string }> = [
  { value: "approver", label: "Approvers" },
  { value: "contributor", label: "Contributors" },
  { value: "viewer", label: "Viewers" },
];

const LABEL_OF_FAMILY = Object.fromEntries(FAMILIES.map((f) => [f.value, f.label]));

/** The three conditions, apart, in the order the server checks them. */
function Conditions({ config }: { config: AssistantConfig }) {
  const { status } = config;
  const rows: ReadonlyArray<{ key: string; label: string; met: boolean; yes: string; no: string }> = [
    { key: "entitled", label: "Add-on", met: status.entitled, yes: "In your agreement", no: "Not in your agreement" },
    { key: "connected", label: "Provider", met: status.connected, yes: "Connected", no: "Not connected" },
    { key: "switched-on", label: "Switch", met: status.switched_on, yes: "On", no: "Off" },
  ];
  return (
    <ul className={own.conditions} aria-label="What has to hold for the assistant to be on" data-testid="assistant-conditions">
      {rows.map((row) => (
        <li key={row.key} className={own.condition} data-condition={row.key} data-met={row.met}>
          <span className={own.conditionLabel}>{row.label}</span>
          <StatusPill tone={row.met ? "ok" : "neutral"}>{row.met ? row.yes : row.no}</StatusPill>
        </li>
      ))}
    </ul>
  );
}

function StatusPanel({ config }: { config: AssistantConfig }) {
  const { status } = config;
  return (
    <Panel
      title="AI assistant"
      sub="Bring your own model"
      column="1 / 13"
      action={<InfoPopover infoKey="legacy.assistant-conditions" />}
    >
      <div className={own.statusRow} data-testid="assistant-status" data-on={status.on}>
        <StatusPill tone={status.on ? "ok" : "neutral"} testId="assistant-state">
          {status.on ? "AI is on" : "AI is off"}
        </StatusPill>
        <Conditions config={config} />
      </div>
      {status.message ? (
        <p className={styles.note} data-testid="assistant-reason" data-code={status.code ?? ""}>
          {status.message}
        </p>
      ) : null}
    </Panel>
  );
}

// ------------------------------------------------------------------------ the provider form

/** A decimal typed by the Admin, kept as text and sent as text (rule 5): `0.003` never becomes a
 * float on the way to the server. Empty means "not set". */
function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function ProviderForm({
  config,
  workspaceRegion,
}: {
  config: AssistantConfig;
  workspaceRegion: string | undefined;
}) {
  const save = useSaveAssistantConfig();
  const [family, setFamily] = useState<Family>(config.adapter_family ?? "openai_compatible");
  const [authStyle, setAuthStyle] = useState<Auth>(config.auth_style ?? "bearer");
  const [endpoint, setEndpoint] = useState(config.endpoint_url ?? "");
  const [model, setModel] = useState(config.model_name ?? "");
  const [region, setRegion] = useState(config.declared_region ?? workspaceRegion ?? "");
  const [currency, setCurrency] = useState(config.currency ?? "");
  const [inRate, setInRate] = useState(config.input_rate_per_million ?? "");
  const [outRate, setOutRate] = useState(config.output_rate_per_million ?? "");
  const [cap, setCap] = useState<CapChoice>(config.cap_choice ?? "unset");
  const [capAmount, setCapAmount] = useState(config.monthly_cap ?? "");
  const [low, setLow] = useState(String(config.soft_threshold_low ?? 50));
  const [high, setHigh] = useState(String(config.soft_threshold_high ?? 80));
  const [roles, setRoles] = useState<ReadonlySet<PermittedRole>>(
    new Set((config.allowed_roles ?? []).filter((r): r is PermittedRole => r === "approver" || r === "contributor" || r === "viewer")),
  );
  const [enabled, setEnabled] = useState(config.enabled);
  const [saved, setSaved] = useState(false);

  // Without the add-on nothing here can be saved (the server refuses with `assistant_not_entitled`),
  // so the form is shown disabled, with the reason beside it, rather than offered and refused.
  const locked = !config.entitled;

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const body: AssistantConfigInput = {
      adapter_family: family,
      auth_style: authStyle,
      endpoint_url: endpoint.trim(),
      model_name: model.trim(),
      declared_region: region.trim(),
      currency: textOrNull(currency),
      input_rate_per_million: textOrNull(String(inRate)),
      output_rate_per_million: textOrNull(String(outRate)),
      cap_choice: cap,
      monthly_cap: cap === "capped" ? textOrNull(String(capAmount)) : null,
      soft_threshold_low: Number.parseInt(low, 10),
      soft_threshold_high: Number.parseInt(high, 10),
      enabled,
      allowed_roles: [...roles],
    };
    save.mutate(body, { onSuccess: () => setSaved(true) });
  }

  function toggleRole(role: PermittedRole) {
    setSaved(false);
    setRoles((current) => {
      const next = new Set(current);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  }

  const touched = () => setSaved(false);

  return (
    <form className={form.form} onSubmit={submit} data-testid="assistant-form">
      <fieldset className={own.set} disabled={locked || save.isPending}>
        <legend className={own.legend}>Provider</legend>
        <div className={own.grid}>
          <label className={form.field}>
            API family
            <select value={family} data-testid="assistant-family" onChange={(e) => { setFamily(e.target.value as Family); touched(); }}>
              {FAMILIES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className={form.field}>
            How the key is sent
            <select value={authStyle} data-testid="assistant-auth" onChange={(e) => { setAuthStyle(e.target.value as Auth); touched(); }}>
              {AUTH_STYLES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className={`${form.field} ${own.wide}`}>
            Endpoint address
            <input type="url" value={endpoint} data-testid="assistant-endpoint" autoComplete="off" spellCheck={false} onChange={(e) => { setEndpoint(e.target.value); touched(); }} />
          </label>
          <label className={form.field}>
            Model name
            <input value={model} data-testid="assistant-model" autoComplete="off" spellCheck={false} onChange={(e) => { setModel(e.target.value); touched(); }} />
          </label>
          <label className={form.field}>
            Region you declare
            <input value={region} data-testid="assistant-region" autoComplete="off" spellCheck={false} onChange={(e) => { setRegion(e.target.value); touched(); }} />
            <span className={form.hint}>Must be this workspace&apos;s own region. EmitIQ cannot check what you declare.</span>
          </label>
        </div>
      </fieldset>

      <fieldset className={own.set} disabled={locked || save.isPending}>
        <legend className={own.legend}>Rates and monthly limit</legend>
        <div className={own.grid}>
          <label className={form.field}>
            Currency
            <input value={currency} maxLength={3} data-testid="assistant-currency" autoComplete="off" spellCheck={false} onChange={(e) => { setCurrency(e.target.value.toUpperCase()); touched(); }} />
          </label>
          <label className={form.field}>
            Input, per million tokens
            <input value={inRate} inputMode="decimal" data-testid="assistant-in-rate" autoComplete="off" onChange={(e) => { setInRate(e.target.value); touched(); }} />
          </label>
          <label className={form.field}>
            Output, per million tokens
            <input value={outRate} inputMode="decimal" data-testid="assistant-out-rate" autoComplete="off" onChange={(e) => { setOutRate(e.target.value); touched(); }} />
          </label>
          <label className={form.field}>
            Monthly limit
            <select value={cap} data-testid="assistant-cap" onChange={(e) => { setCap(e.target.value as CapChoice); touched(); }}>
              {CAPS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          {cap === "capped" ? (
            <label className={form.field}>
              Limit amount
              <input value={capAmount} inputMode="decimal" data-testid="assistant-cap-amount" autoComplete="off" onChange={(e) => { setCapAmount(e.target.value); touched(); }} />
            </label>
          ) : null}
          <label className={form.field}>
            Warn at, percent
            <input value={low} inputMode="numeric" data-testid="assistant-low" onChange={(e) => { setLow(e.target.value); touched(); }} />
          </label>
          <label className={form.field}>
            And again at, percent
            <input value={high} inputMode="numeric" data-testid="assistant-high" onChange={(e) => { setHigh(e.target.value); touched(); }} />
          </label>
        </div>
        <span className={form.hint}>Spend is estimated from these rates.</span>
      </fieldset>

      <fieldset className={own.set} disabled={locked || save.isPending}>
        <legend className={own.legend}>Who may use it</legend>
        <div className={own.checks}>
          <span className={own.check}>
            <input type="checkbox" checked disabled readOnly id="assistant-role-admin" />
            <label htmlFor="assistant-role-admin">Admins (always)</label>
          </span>
          {ROLES.map((role) => (
            <span key={role.value} className={own.check}>
              <input
                type="checkbox"
                id={`assistant-role-${role.value}`}
                checked={roles.has(role.value)}
                data-testid={`assistant-role-${role.value}`}
                onChange={() => toggleRole(role.value)}
              />
              <label htmlFor={`assistant-role-${role.value}`}>{role.label}</label>
            </span>
          ))}
        </div>
        <span className={form.hint}>Auditors never use it.</span>
      </fieldset>

      <span className={own.check}>
        <input
          type="checkbox"
          id="assistant-enabled"
          checked={enabled}
          disabled={locked || save.isPending}
          data-testid="assistant-enabled"
          onChange={(e) => { setEnabled(e.target.checked); touched(); }}
        />
        <label htmlFor="assistant-enabled">Switch the assistant on</label>
      </span>

      <WriteFailed error={save.error} of="the AI assistant's settings" />
      {saved && !save.isPending ? (
        <p className={form.saved} role="status" data-testid="assistant-saved">Saved.</p>
      ) : null}
      <span>
        <button type="submit" className={styles.primary} disabled={locked || save.isPending} data-testid="assistant-save">
          {save.isPending ? "Saving…" : "Save settings"}
        </button>
      </span>
    </form>
  );
}

// ------------------------------------------------------------------------ the key

/** The day the key was saved, in the reader's zone. **Its own component** so the reader's preferences
 * are asked for only when there is a key to date, never on a workspace that has none. */
function KeySince({ at }: { at: string }) {
  const reader = useReader();
  return <span className={own.meta}>since {instantDate(at, reader)}</span>;
}

function KeyPanel({ config }: { config: AssistantConfig }) {
  const put = useSetAssistantCredential();
  const remove = useRemoveAssistantCredential();
  const test = useAssistantConnectionTest();
  const [key, setKey] = useState("");
  const [checks, attach] = useFormChecks();
  // After a refusal (the vault is not configured, a stale session) focus goes to its sentence.
  useFocusRefusal(checks.element, put.error);

  function submit(event: FormEvent) {
    event.preventDefault();
    // An empty box is a sentence beside it, not a grey button (Q-06). The key itself is never put in
    // a message, a query key or a log: only whether there is one.
    if (!checks.run([{ field: "key", ok: key !== "", message: "Paste the provider key, then save." }])) return;
    put.mutate(key, { onSuccess: () => setKey("") });
  }

  return (
    <Panel title="Provider key" sub="Write-only" column="1 / 7">
      <div className={own.keyState} data-testid="assistant-key-state" data-set={config.credential_set}>
        <StatusPill tone={config.credential_set ? "ok" : "neutral"}>
          {config.credential_set ? "Key saved" : "No key saved"}
        </StatusPill>
        {config.credential_set && config.credential_set_at ? (
          <KeySince at={config.credential_set_at} />
        ) : null}
      </div>
      <form ref={attach} className={form.form} onSubmit={submit} noValidate>
        <label className={form.field}>
          {config.credential_set ? "Replace the key" : "Provider key"}
          <input
            type="password"
            value={key}
            autoComplete="off"
            spellCheck={false}
            disabled={!config.entitled || put.isPending}
            {...checks.field("key")}
            data-testid="assistant-key"
            onChange={(e) => {
              checks.touch("key");
              setKey(e.target.value);
            }}
          />
          <FieldError {...checks.say("key")} />
          <span className={form.hint}>It is stored, never shown again, and never sent to this page.</span>
        </label>
        <WriteFailed error={put.error} of="the key" />
        <span className={own.actions}>
          <button type="submit" className={styles.primary} disabled={!config.entitled || put.isPending} data-testid="assistant-key-save">
            {put.isPending ? "Saving…" : "Save key"}
          </button>
          {config.credential_set ? (
            <button type="button" className={styles.secondary} disabled={remove.isPending} data-testid="assistant-key-remove" onClick={() => remove.mutate()}>
              {remove.isPending ? "Removing…" : "Disconnect"}
            </button>
          ) : null}
        </span>
        <WriteFailed error={remove.error} of="the disconnect" />
      </form>
      <div className={own.test}>
        <button type="button" className={styles.secondary} disabled={!config.entitled || !config.credential_set || test.isPending} data-testid="assistant-test" onClick={() => test.mutate()}>
          {test.isPending ? "Testing…" : "Test the connection"}
        </button>
        {test.data ? (
          <p className={styles.note} role="status" data-testid="assistant-test-result" data-state={test.data.state} data-code={test.data.code ?? ""}>
            {test.data.state === "answered" ? "The provider answered." : (test.data.message ?? "The test was refused.")}
          </p>
        ) : null}
        <WriteFailed error={test.error} of="the test" />
      </div>
    </Panel>
  );
}

// ------------------------------------------------------------------------ the spend

function money(currency: string | null, amount: string): string {
  return currency ? `${currency} ${amount}` : amount;
}

function Tally({
  rows,
  head,
  person,
}: {
  rows: AssistantSpend["by_feature"];
  head: string;
  person?: boolean;
}) {
  if (rows.length === 0) return null;
  return (
    <table className={own.table}>
      <thead>
        <tr>
          <th scope="col">{head}</th>
          <th scope="col" className={own.num}>Calls</th>
          <th scope="col" className={own.num}>Tokens in</th>
          <th scope="col" className={own.num}>Tokens out</th>
          <th scope="col" className={own.num}>Estimated</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <th scope="row">{person ? <PersonRef userId={row.label} showName /> : row.label.replaceAll("_", " ")}</th>
            <td className={own.num}>{row.tally.calls}</td>
            <td className={own.num}>{row.tally.tokens_in}</td>
            <td className={own.num}>{row.tally.tokens_out}</td>
            <td className={own.num}>{money(row.tally.currency, row.tally.estimated)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SpendPanel() {
  const spend = useAssistantSpend(true);
  return (
    <Panel
      title="This month's spend"
      sub="Calendar month, UTC"
      column="7 / 13"
      action={
        <a className={styles.secondary} href={apiUrl(ASSISTANT_SPEND_EXPORT)} download data-testid="assistant-spend-export">
          Export CSV
        </a>
      }
    >
      <Loaded query={spend} of="This month's spend">
        {(value) => (
          <div data-testid="assistant-spend">
            <dl className={own.spendFacts}>
              <div>
                <dt>Month to date</dt>
                <dd data-testid="assistant-spend-total">{money(value.currency, value.month_to_date)}</dd>
              </div>
              <div>
                <dt>Limit</dt>
                <dd data-testid="assistant-spend-cap">
                  {value.cap_choice === "capped" && value.cap
                    ? money(value.currency, value.cap)
                    : value.cap_choice === "no_cap"
                      ? "No cap"
                      : "Not decided"}
                </dd>
              </div>
              <div>
                <dt>Warnings reached</dt>
                <dd>{value.thresholds_reached.length ? value.thresholds_reached.map((n) => `${n}%`).join(", ") : "None"}</dd>
              </div>
            </dl>
            <p className={styles.note} data-testid="assistant-spend-basis">
              {value.basis}
            </p>
            <Tally rows={value.by_feature} head="Feature" />
            <Tally rows={value.by_user} head="Person" person />
            {Object.keys(value.by_outcome).length ? (
              <p className={styles.note}>
                Outcomes:{" "}
                {Object.entries(value.by_outcome)
                  .map(([outcome, n]) => `${outcome.replaceAll("_", " ")} ${n}`)
                  .join(", ")}
                .
              </p>
            ) : (
              <p className={styles.note}>Nothing has been asked this month.</p>
            )}
          </div>
        )}
      </Loaded>
    </Panel>
  );
}

// ------------------------------------------------------------------------ the screen

function AssistantSettingsBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const config = useAssistantConfig(isAdmin);
  const organisation = useOrganization();

  if (!isAdmin) {
    return (
      <ScreenFrame screen={screen} title="AI assistant" crumb="provider, spend limit and who may use it">
        <Panel title="AI assistant" column="1 / 8">
          <p className={styles.note} data-testid="assistant-admin-only">
            An <strong>Admin</strong> connects a model and sets its spend limit. Whether the assistant
            is on for you is shown where you would use it.
          </p>
        </Panel>
      </ScreenFrame>
    );
  }

  return (
    <ScreenFrame screen={screen} title="AI assistant" crumb="provider, spend limit and who may use it" bodyScrolls>
      <Loaded query={config} of="The AI assistant's settings">
        {(value) => (
          <>
            <StatusPanel config={value} />
            <Panel
              title="Provider and limit"
              sub={value.configured && value.adapter_family ? `${LABEL_OF_FAMILY[value.adapter_family] ?? value.adapter_family}, version ${value.version ?? 1}` : "Not set up"}
              column="1 / 13"
            >
              {/* Keyed on the region as well, so the declared region starts as the workspace's own
                  once it has been read, and never as an empty box the Admin must guess into. */}
              <ProviderForm
                key={`${value.version ?? 0}:${organisation.data?.data_region ?? "pending"}`}
                config={value}
                workspaceRegion={organisation.data?.data_region}
              />
            </Panel>
            <KeyPanel config={value} />
            <SpendPanel />
          </>
        )}
      </Loaded>
    </ScreenFrame>
  );
}

export function AssistantSettingsScreen() {
  return (
    <RequireSession>
      <AssistantSettingsBody />
    </RequireSession>
  );
}
